import Navigation from './modules/navigation';
import QuickSearch from './modules/quick-search';
import SiteSearch from './modules/site-search';
import seasonDependingContent from '../../../common/build/helpers/season-depending';
import buyingOptionsBar from './modules/buying-options-bar';
import locationPrompts from './modules/location-prompts';
import FileUploader from './modules/file-uploader';
import { initFormAnalytics } from './modules/form-analytics';
import videoModal from './modules/video-modal';

declare global {
  interface Window {
    Hatch?: {
      init?: () => void;
    };
    hatchInit?: string;
  }
}

import { common, toggle } from '@nt-workspace/common';

document.addEventListener("DOMContentLoaded", function (event) {
  // Core website functionality, loaded always

  common.lazyLoadMedia.init();
  common.video.init();
  locationPrompts.init();
  videoModal.init();

  document.querySelectorAll('.js-file-uploader').forEach(fileInput => {
    new FileUploader(fileInput as HTMLInputElement);
  });

  if (window.Hatch && typeof window.Hatch.init === 'function') {
    window.Hatch.init();
  }

  buyingOptionsBar.init();
  initFormAnalytics();

  new seasonDependingContent();

  const navEl = document.querySelector('[data-main-navigation]') as HTMLElement;
  if (navEl) {
    new Navigation(navEl);
  }

  const quickSearchEl = document.querySelector('[data-quick-search]') as HTMLElement;
  if (quickSearchEl) {
    new QuickSearch(quickSearchEl, {
      site: quickSearchEl.dataset.site || '',
    });
  }

  const siteSearchEl = document.querySelectorAll('.js-site-search');
  if(siteSearchEl.length) {
    siteSearchEl.forEach((el) => {
      new SiteSearch(el as HTMLElement);
    });
  }

  const toggles = document.querySelectorAll('.js-toggle');
  if (toggles.length) {
    for (let toggleEl of toggles) {
      toggle.initToggle(toggleEl as HTMLElement);
    }
  }

  // Component functionality, loaded only when needed

  const accordionTabs = document.querySelectorAll('.js-accordion-tabs');
  if (accordionTabs.length) {
    import(/* webpackChunkName: "accordionTabs", webpackMode: "lazy" */ '../../../common/build/helpers/accordionTabs').then(m => {
      for (let element of accordionTabs) {
        m.default.init(element as HTMLElement);
      }
    });
  }

  const tooltips = document.getElementsByClassName('js-tooltip');
  if (tooltips.length) {
    import(/* webpackChunkName: "tooltip", webpackMode: "lazy" */ '../../../common/build/helpers/tooltip').then(m => {
      for (let collection of tooltips) {
        m.init(collection as HTMLElement);
      }
    });
  }

  const contentCardsliders = document.getElementsByClassName('js-content-card-slider');
  if (contentCardsliders.length) {
    import(/* webpackChunkName: "slider", webpackMode: "lazy" */ './modules/content-card-slider').then(({ init }) => {
      for (let element of contentCardsliders) {
        init(element as HTMLElement);
      }
    });
  }

  // Product elements

  const productSliderElements = document.querySelectorAll('.product-slider');
  if (productSliderElements.length) {
    import(/* webpackChunkName: "product-slider", webpackMode: "lazy" */'./modules/product-slider/product-slider').then(m => { // jshint ignore:line
      m.init(productSliderElements);
    });
  }

  // Hatch
  const hatch = window.hatchInit === '1';
  if (hatch) {
    import(/* webpackChunkName: "hatch", webpackMode: "lazy" */'./modules/hatch').then(m => { // jshint ignore:line
      m.default.init();
    });
  }

  const productAwardSliders = document.getElementsByClassName('js-product-awards-slider');
  if (productAwardSliders.length) {
    import(/* webpackChunkName: "slider", webpackMode: "lazy" */ './modules/products/product-awards-slider').then(({ init }) => {
      for (let element of productAwardSliders) {
        init(element as HTMLElement);
      }
    });
  }

  if (document.querySelectorAll('.reviews').length > 0) {
    import(/* webpackChunkName: "reviews", webpackMode: "lazy" */ './modules/products/reviews').then(m => {
      m.default.init();
    });
  }

  const tireSizes = document.querySelector('.tire-sizes');
  if (tireSizes) {
    import(/* webpackChunkName: "tire-sizes", webpackMode: "lazy" */'./modules/products/tire-sizes').then(m => { // jshint ignore:line
      m.default.init();
    });
  }

  // Modals

  const a11ymodals = document.querySelectorAll('[data-modal], [data-modal-v2]');
  if (a11ymodals.length) {
    import(/* webpackChunkName: "tooltip", webpackMode: "lazy" */ '../../../common/build/helpers/a11y-modal').then(m => {
      m.default.init(a11ymodals);
    });
  }

  // Forms

  const phoneInputs = document.querySelectorAll('input.js-phone-input');
  if(phoneInputs.length) {
    import(/* webpackChunkName: "phoneInput", webpackMode: "lazy" */ './modules/intl-tel-input/Intl-tel-input').then(m => {
      for(let element of phoneInputs) {
        m.init(element as HTMLInputElement);
      }
    });
  }

  const comboDateInputs = document.querySelectorAll('.js-combodate');
  if(comboDateInputs.length) {
    import(/* webpackChunkName: "input-combodate", webpackMode: "lazy" */'./modules/combodate/combodate').then(m => { // jshint ignore:line
      for(let element of comboDateInputs) {
        m.init(element as HTMLInputElement);
      }
    });
  }

  const guaranteeForms = document.querySelectorAll('select[name="guarantee_tyre_model"]');
  if(guaranteeForms.length) {
    import(/* webpackChunkName: "guarantee-form", webpackMode: "lazy" */'./modules/products/guarantee-form').then(m => {
      for(let element of guaranteeForms) {
        m.init(element as HTMLSelectElement);
      }
    });
  }

  // Old hakkaturva
  const hakkaForm = document.getElementById('rengasmalli')?.closest('form');
  if(hakkaForm?.length) {
    import(/* webpackChunkName: "hakka-form", webpackMode: "lazy" */'./modules/products/hakka-form').then(m => {
      m.default.init(hakkaForm as HTMLFormElement);
    });
  }

  const dotForms = document.querySelectorAll('[data-dot-form]');
  if(dotForms.length) {
    import(/* webpackChunkName: "nt-dot-form", webpackMode: "lazy" */'./modules/dot-form/dot-form').then(({init}) => { // jshint ignore:line
      for(let element of dotForms) {
        init(element as HTMLElement);
      }
    });
  }

  const feedbackTopics = document.querySelectorAll('.js-form__feedback-subjects');
  if(feedbackTopics.length) {
    import(/* webpackChunkName: "feedback-topics", webpackMode: "lazy" */'./modules/feedback-topics').then(m => { // jshint ignore:line
      for(let element of feedbackTopics) {
        m.init(element as HTMLSelectElement);
      }
    });
  }

  const gotoEls = document.querySelectorAll('.js-goto');
  if(gotoEls.length) {
    import(/* webpackChunkName: "go-to", webpackMode: "lazy" */'./modules/goto').then(({init}) => {
      for(let element of gotoEls) {
        init(element as HTMLElement);
      }
    });
  }

  const triggerAnalyticsEls = document.querySelectorAll('.js-trigger-analytics') as NodeListOf<HTMLElement>;
  if(triggerAnalyticsEls.length) {
    import(/* webpackChunkName: "trigger-analytics", webpackMode: "lazy" */'./modules/trigger-analytics').then((m) => {
      m.default.init(triggerAnalyticsEls);
    });
  }

  const triggerAnalyticsV2Els = document.querySelectorAll('[data-gtm-event]') as NodeListOf<HTMLElement>;
  if(triggerAnalyticsV2Els.length) {
    import(/* webpackChunkName: "trigger-analytics", webpackMode: "lazy" */'./modules/trigger-analytics-v2').then((m) => {
      m.default.init(triggerAnalyticsV2Els);
    });
  }

  const mediaCarousels = document.querySelectorAll('.js-media-carousel');
  if(mediaCarousels.length) {
    import(/* webpackChunkName: "feedback-topics", webpackMode: "lazy" */'../../../common/build/components/media-carousel').then(m => { // jshint ignore:line
      for(let element of mediaCarousels) {
        m.default.initMediaCarousel(element as HTMLElement);
      }
    });
  }

  // Dealer map (react app)
  if(document.querySelectorAll('#dealers').length){
    import(/* webpackChunkName: "dealers-react", webpackMode: "lazy" */'./dealers').then(m => {
      console.log('dealers.init()');
    });
  }

  const csEls = document.querySelectorAll('[data-customer-service]') as NodeListOf<HTMLElement>;
  if(csEls.length) {
    import(/* webpackChunkName: "customer-service", webpackMode: "lazy" */'./modules/customer-service').then(({init}) => {
      init(csEls);
    });
  }

  const timeToRead = document.querySelectorAll('.js-time-to-read');
  if (timeToRead.length) {
    import(/* webpackChunkName: "timeToRead", webpackMode: "lazy" */'../../../common/build/helpers/timeToRead').then(m => { // jshint ignore:line
      timeToRead.forEach((el) => {
        m.default.init((el as HTMLElement));
      });
    });
  }

  const interactiveImages = document.getElementsByClassName('js-interactive-image');
  if(interactiveImages.length) {
    import(/* webpackChunkName: "interactive-image", webpackMode: "lazy" */'./modules/interactive-image').then(m => {
      for (let image of interactiveImages) {
        m.init(image);
      }
    });
  }

  // Dealer list
  if(document.querySelectorAll('.dealer-list').length){
    import(/* webpackChunkName: "dealer-list", webpackMode: "lazy" */'./modules/dealer-list').then(m => { // jshint ignore:line
      m.default.init();
    });
  }

  const tsElements = document.querySelectorAll('[data-ts]') as NodeListOf<HTMLElement>;
  if(tsElements.length) {
    import(/* webpackChunkName: "tyreselector", webpackMode: "lazy" */'./modules/tyreselector/Tyreselector').then(({ init }) => {
      init(tsElements);
    });
  }

  const comboboxElements = document.querySelectorAll('.js-combobox-autocomplete') as NodeListOf<HTMLSelectElement>;
  if(comboboxElements.length) {
    import(/* webpackChunkName: "combobox-autocomplete", webpackMode: "lazy" */'./modules/combobox/autocomplete-combobox').then((m) => {
      for(let element of comboboxElements) {
        new m.default({
          select: element,
        });
      }
    });
  }

  const gdprElements = document.querySelectorAll('.js-gdpr') as NodeListOf<HTMLElement>;
  if(gdprElements.length) {
    import(/* webpackChunkName: "gdpr", webpackMode: "lazy" */'./modules/gdpr').then((m) => {
      m.default.init(gdprElements);
    });
  }

  const cmTyreselectors = document.getElementsByClassName('js-cm-tyreselector');
  if(cmTyreselectors.length) {
    import(/* webpackChunkName: "nt-cm-tyreselector", webpackMode: "lazy" */'./modules/car-manufacturers/cm-tyreselector').then(({init}) => {
      for (let collection of cmTyreselectors) {
        init(collection as HTMLElement);
      }
    });
  }

  const tyreAwards = document.getElementsByClassName('js-tyre-awards');
  if(tyreAwards.length) {
    import(/* webpackChunkName: "tyre-awards", webpackMode: "lazy" */'./modules/tyre-awards').then((m) => {
      for (let awards of tyreAwards) {
        m.default.init(awards as HTMLElement);
      }
    });
  }

  const countdownElements = document.querySelectorAll('[data-countdown-date]');
  if(countdownElements.length) {
    import(/* webpackChunkName: "nt-countdown", webpackMode: "lazy" */'./modules/countdown').then(m => { // jshint ignore:line
      m.init(countdownElements);
    });
  }

  const tabbedContentElements = document.querySelectorAll('[data-tabbed-content]');
  if(tabbedContentElements.length) {
    import(/* webpackChunkName: "tabbed-content", webpackMode: "lazy" */'./modules/tabs').then(m => { // jshint ignore:line
      for (let tabsElement of tabbedContentElements) {
        new m.default(tabsElement as HTMLElement);
      }
    });
  }

  const paginatedForms = document.querySelectorAll('[data-paginated-form]');
  if(paginatedForms.length) {
    import(/* webpackChunkName: "form-paginator", webpackMode: "lazy" */'./modules/form-paginator').then(m => {
      for (let form of paginatedForms) {
        new m.default(form as HTMLFormElement);
      }
    });
  }

  const careersElement = document.querySelector('.section-careers');
  if (careersElement) {
    import(/* webpackChunkName: "careers", webpackMode: "lazy" */'./modules/careers').then(m => { // jshint ignore:line
      m.default.init(careersElement as HTMLElement);
    });
  }

  const youtubeSliders = document.querySelectorAll('[data-youtube-video-slider]');
  if(youtubeSliders.length) {
    import(/* webpackChunkName: "youtube-video-slider", webpackMode: "lazy" */'./modules/youtube-video-slider').then(m => {
      for (let element of youtubeSliders) {
        new m.default(element as HTMLElement);
      }
    });
  }

  const listingFilters = document.querySelectorAll('[data-listing-filters]') as NodeListOf<HTMLElement>;
  if(listingFilters.length) {
    import(/* webpackChunkName: "listing-filters", webpackMode: "lazy" */'./modules/listing-filters').then(({ init }) => {
      init(listingFilters);
    });
  }

  // Helper for file inputs
  const fileInputs = document.querySelectorAll('input[type="file"][data-value-field]');
  if(fileInputs.length) {
    console.log('init fileInput');

    import(/* webpackChunkName: "file-input", webpackMode: "lazy" */'./modules/file-input').then((m) => {
      for(let input of fileInputs) {
        m.default.init(input as HTMLInputElement);
      }
    });
  }

  // Animated
  const animatedElements = document.querySelectorAll('.template-animated [data-animate]');
  if(animatedElements.length) {
    import(/* webpackChunkName: "animated", webpackMode: "lazy" */'./modules/animated').then(m  => {
      m.init(animatedElements);
    });
  }

  const genericSliders = document.querySelectorAll('.js-slider');
  if(genericSliders.length) {
    import(/* webpackChunkName: "slider", webpackMode: "lazy" */'./modules/slider/Slider').then(m => {
      m.init(genericSliders as NodeListOf<HTMLElement>);
    });
  }

  const unitSystemToggles = document.querySelectorAll('[data-unit-system-container]');
  if(unitSystemToggles.length) {
    import(/* webpackChunkName: "unit-system-toggle", webpackMode: "lazy" */'./modules/unit-system-toggle').then(m => {
      for (let element of unitSystemToggles) {
        new m.default(element as HTMLElement);
      }
    });
  }

  const imageSliders = document.querySelectorAll('[data-image-slider]');
  if (imageSliders.length) {
    import(/* webpackChunkName: "image-slider", webpackMode: "lazy" */ './modules/image-slider').then(m => {
      for (let element of imageSliders) {
        new m.default(element as HTMLElement);
      }
    });
  }

  const anchorNavElements = document.querySelectorAll<HTMLElement>('[data-anchor-navigation]');
  if(anchorNavElements.length) {
    import(/* webpackChunkName: "acnhor-navigation", webpackMode: "lazy" */'./modules/anchor-navigation').then(m => {
      m.init(anchorNavElements);
    });
  }

  const goBackEls = document.querySelectorAll('.js-go-back');
  if(goBackEls.length) {
    import(/* webpackChunkName: "go-back", webpackMode: "lazy" */'./modules/go-back').then(({init}) => {
      for(let element of goBackEls) {
        init(element as HTMLElement);
      }
    });
  }

  const rnsElements = document.querySelectorAll('[data-rns]');
  if(rnsElements.length) {
    import(/* webpackChunkName: "rns", webpackMode: "lazy" */'./modules/react-and-share').then(m => {
      for (let element of rnsElements) {
        new m.default(element as HTMLElement);
      }
    });
  }

  // NHT Elements
  const wheelsListings = document.querySelectorAll('[data-wheels-listing]');
  if (wheelsListings.length) {
    import(/* webpackChunkName: "nht-wheels-listing", webpackMode: "lazy" */'./modules/nht/wheels-listing').then(m => {
      m.init(wheelsListings as NodeListOf<HTMLElement>);
    });
  }

  const nhtProductSelectorEmbeds = document.querySelectorAll('[data-nht-product-selector-embed]');
  if(nhtProductSelectorEmbeds.length) {
    import(/* webpackChunkName: "nht-product-selector-embed", webpackMode: "lazy" */'./modules/nht/product-selector-embed/product-selector').then(m => { // jshint ignore:line
      m.init(nhtProductSelectorEmbeds);
    });
  }

  const nhtNewsletterForms = document.querySelectorAll('[data-nht-newsletter-form]');
  if(nhtNewsletterForms.length) {
    import(/* webpackChunkName: "nht-newsletter", webpackMode: "lazy" */'./modules/nht/nht-newsletter').then(m => {
      m.init(nhtNewsletterForms as NodeListOf<HTMLFormElement>);
    });
  }

  const nhtHeavyStories = document.querySelectorAll('[data-heavy-stories]');
  if(nhtHeavyStories.length) {
    import(/* webpackChunkName: "nht-heavy-stories", webpackMode: "lazy" */'./modules/nht/heavy-stories').then(m => {
      for (let element of nhtHeavyStories) {
        new m.default(element as HTMLElement);
      }
    });
  }

  const nhtNews = document.querySelectorAll('[data-nht-news]');
  if(nhtNews.length) {
    import(/* webpackChunkName: "nht-news", webpackMode: "lazy" */'./modules/nht/nht-news').then(m => {
      for (let element of nhtNews) {
        new m.default(element as HTMLElement);
      }
    });
  }

  const nhtTreadSelectors = document.querySelectorAll('[data-nht-tread-selector]');
  if(nhtTreadSelectors.length) {
    import(/* webpackChunkName: "nht-tread-selector", webpackMode: "lazy" */'./modules/nht/product-selector/tread-selector').then(m => {
      for (let element of nhtTreadSelectors) {
        new m.default(element as HTMLElement);
      }
    });
  }

  const nhtTireSelectors = document.querySelectorAll('[data-nht-tire-selector]');
  if(nhtTireSelectors.length) {
    import(/* webpackChunkName: "nht-tire-selector", webpackMode: "lazy" */'./modules/nht/product-selector/tire-selector').then(m => {
      for (let element of nhtTireSelectors) {
        new m.default(element as HTMLElement);
      }
    });
  }

  const heroAnimate = document.querySelectorAll('.js-hero-animate');
  if(heroAnimate.length) {
    import(/* webpackChunkName: "js-hero-animate", webpackMode: "lazy" */'../../../common/build/helpers/hero-animate').then(m => {
        m.default.init();
    });
  }

  const releases = document.querySelectorAll('[data-releases]');
  if(releases.length) {
    import(/* webpackChunkName: "releases", webpackMode: "lazy" */'./modules/releases').then(m => {
      for (let element of releases) {
        new m.default.init(element as HTMLElement);
      }
    });
  }
});
